import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PosterCard from "@/components/base/PosterCard";
import Pagination from "@/components/base/Pagination";
import FilterSidebar, { type FilterState } from "./components/FilterSidebar";
import { movies } from "@/mocks/movies";
import { tvs } from "@/mocks/tvs";
import { PAGE_SIZE } from "@/mocks/filters";
import { toSummary, type MediaSummary } from "@/utils/media";
import { useSeo } from "@/hooks/useSeo";
import type { MediaType } from "@/types/media";

interface CategoryPageProps {
  type: MediaType;
}

export default function CategoryPage({ type }: CategoryPageProps) {
  const isMovie = type === "movie";
  const label = isMovie ? "电影" : "电视剧";
  const [searchParams, setSearchParams] = useSearchParams();

  const filter: FilterState = {
    genres: (searchParams.get("genre") || "").split(",").filter(Boolean),
    region: searchParams.get("region") || "全部",
    year: searchParams.get("year") || "all",
    sort: searchParams.get("sort") || "hot",
  };
  const page = Math.max(1, Number(searchParams.get("page")) || 1);

  const [loading, setLoading] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  useSeo({
    title: `${label} - 天堂电影网`,
    description: isMovie
      ? "天堂电影网电影频道，收录最新动作、喜剧、科幻等各类电影，支持按类型、地区、年份多条件筛选浏览。"
      : "天堂电影网电视剧频道，汇集都市、悬疑、古装、科幻等热播剧集，实时显示更新进度，支持多条件筛选。",
    keywords: `天堂电影网${label},在线观看${label},${label}筛选,高清${label}推荐`,
  });

  const baseList: MediaSummary[] = useMemo(() => {
    if (isMovie) return movies.map((item) => toSummary(item, "movie"));
    return tvs.map((item) => toSummary(item, "tv"));
  }, [isMovie]);

  const filtered = useMemo(() => {
    let list = [...baseList];
    if (filter.genres.length > 0) {
      list = list.filter((item) => item.genres.some((g) => filter.genres.includes(g)));
    }
    if (filter.region !== "全部") {
      list = list.filter((item) => item.country === filter.region);
    }
    if (filter.year !== "all") {
      list = list.filter((item) =>
        filter.year === "older" ? item.year <= 2020 : String(item.year) === filter.year
      );
    }
    if (filter.sort === "rating") {
      list.sort((a, b) => b.rating - a.rating);
    } else if (filter.sort === "updated") {
      list.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
    } else {
      list.sort((a, b) => b.rating - a.rating);
    }
    return list;
  }, [baseList, filter.genres, filter.region, filter.year, filter.sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const filterKey = `${filter.genres.join(",")}|${filter.region}|${filter.year}|${filter.sort}|${currentPage}`;

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 260);
    return () => window.clearTimeout(timer);
  }, [filterKey]);

  const updateParams = (params: Record<string, string | undefined>, resetPage = true) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(params).forEach(([key, val]) => {
      if (!val || val === "全部" || val === "all" || val === "") next.delete(key);
      else next.set(key, val);
    });
    if (resetPage) next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const handleFilterChange = (next: FilterState) => {
    updateParams({
      genre: next.genres.join(","),
      region: next.region,
      year: next.year,
      sort: next.sort === "hot" ? undefined : next.sort,
    });
  };

  const handleReset = () => {
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  const handlePageChange = (nextPage: number) => {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete("page");
    else next.set("page", String(nextPage));
    setSearchParams(next, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <SiteHeader />
      <main className="flex-1 pt-16 md:pt-20">
        <div className="w-full px-4 md:px-8 pt-8 md:pt-10">
          <div className="max-w-[1400px] mx-auto">
            <nav className="flex items-center gap-1.5 text-xs md:text-sm text-foreground-500" aria-label="面包屑">
              <a href="/" className="hover:text-accent-300 transition-colors cursor-pointer">
                首页
              </a>
              <i className="ri-arrow-right-s-line" aria-hidden="true" />
              <span className="text-foreground-800">{label}</span>
            </nav>
            <div className="mt-4 flex items-end gap-3">
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950">{label}频道</h1>
              <span className="pb-1.5 text-sm text-foreground-500">共 {filtered.length} 部作品</span>
            </div>
            <p className="mt-2 text-sm text-foreground-400 max-w-2xl">
              {isMovie
                ? "收录最新上映与影史经典电影，支持按类型、地区、年份与排序自由组合筛选，快速找到想看的那一部。"
                : "汇集各大平台热播电视剧，实时同步更新进度，可按类型、地区、年份与排序多条件筛选追更。"}
            </p>
          </div>
        </div>

        <div className="w-full px-4 md:px-8 py-8 md:py-10">
          <div className="max-w-[1400px] mx-auto flex flex-col lg:flex-row gap-6">
            <div className="hidden lg:block">
              <FilterSidebar type={type} value={filter} onChange={handleFilterChange} onReset={handleReset} />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-foreground-500">
                  共找到 <span className="text-accent-300 font-medium">{filtered.length}</span> 部作品
                </p>
                <button
                  type="button"
                  onClick={() => setMobileFilterOpen(true)}
                  className="lg:hidden inline-flex items-center gap-1.5 rounded-md border border-background-300/60 px-3 py-2 text-sm text-foreground-800 cursor-pointer"
                >
                  <i className="ri-filter-3-line" aria-hidden="true" />
                  筛选
                </button>
              </div>

              {loading ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                  {Array.from({ length: 8 }).map((_, index) => (
                    <div key={index} className="animate-pulse">
                      <div className="poster-ratio w-full rounded-lg bg-background-200" />
                      <div className="mt-3 h-3 w-3/4 rounded bg-background-200" />
                      <div className="mt-2 h-3 w-1/2 rounded bg-background-200" />
                    </div>
                  ))}
                </div>
              ) : pageItems.length === 0 ? (
                <div className="rounded-lg bg-background-100 ring-1 ring-background-200/70 py-20 px-6 text-center">
                  <span className="mx-auto w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-400">
                    <i className="ri-emotion-sad-line" aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-900">没有找到匹配的作品</h3>
                  <p className="mt-2 text-sm text-foreground-500">试试调整筛选条件，或重置后重新浏览全部内容。</p>
                  <button
                    type="button"
                    onClick={handleReset}
                    className="mt-5 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-refresh-line" aria-hidden="true" />
                    重置筛选条件
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                    {pageItems.map((item) => (
                      <PosterCard key={item.id} item={item} />
                    ))}
                  </div>
                  <Pagination current={currentPage} totalPages={totalPages} onChange={handlePageChange} />
                </>
              )}
            </div>
          </div>
        </div>
      </main>

      {mobileFilterOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setMobileFilterOpen(false)}
            className="absolute inset-0 bg-background-50/70 backdrop-blur-sm cursor-pointer"
          />
          <div className="absolute right-0 top-0 h-full w-[85%] max-w-sm bg-background-50 overflow-y-auto p-4 animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <span className="font-heading text-lg font-semibold text-foreground-950">筛选条件</span>
              <button
                type="button"
                onClick={() => setMobileFilterOpen(false)}
                aria-label="关闭"
                className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300/60 text-foreground-800 cursor-pointer"
              >
                <i className="ri-close-line text-xl" aria-hidden="true" />
              </button>
            </div>
            <FilterSidebar type={type} value={filter} onChange={handleFilterChange} onReset={handleReset} />
            <button
              type="button"
              onClick={() => setMobileFilterOpen(false)}
              className="mt-6 w-full rounded-md bg-primary-500 py-3 text-sm font-medium text-background-50 cursor-pointer"
            >
              查看结果（{filtered.length}）
            </button>
          </div>
        </div>
      )}

      <SiteFooter />
    </div>
  );
}